import { Bell, BellSlash, X, Warning } from "@phosphor-icons/react";
import { usePushNotification } from "../lib/usePushNotification";
import { useAuth } from "../contexts/AuthContext";
import { useState } from "react";

/**
 * Banner global de notificaciones push.
 * En iOS: muestra instrucciones si no está en modo standalone.
 * Se puede cerrar y se guarda en localStorage.
 */
export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const { supported, permission, subscribed, loading, error, isIOS, isStandalone, subscribe, unsubscribe } =
    usePushNotification(user?.id);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("push_prompt_dismissed") === "1"
  );

  const handleDismiss = () => {
    localStorage.setItem("push_prompt_dismissed", "1");
    setDismissed(true);
  };

  // On iOS not standalone: show the install instructions banner (dismissible)
  const isIOSNotStandalone = isIOS && !isStandalone;
  if (isIOSNotStandalone && !dismissed) {
    return (
      <div
        data-testid="push-ios-install-banner"
        className="flex items-start gap-3 px-4 py-3 rounded-xl text-sm"
        style={{ background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.2)" }}
      >
        <Warning weight="fill" className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <span className="text-white/90 font-medium">Activa notificaciones en iPhone:</span>
          <span className="text-white/55 ml-2">
            Pulsa Compartir → "Agregar a pantalla de inicio" → abre la app desde ahí
          </span>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-white/30 hover:text-white/60 transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // No mostrar si no hay soporte, ya está suscrito, permisos bloqueados, o fue descartado
  if (!supported || permission === "denied" || dismissed) return null;

  if (subscribed) {
    return (
      <div
        data-testid="push-subscribed-badge"
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
        style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", color: "#34d399" }}
      >
        <Bell className="w-3.5 h-3.5" weight="fill" />
        Notificaciones activadas
        <button onClick={unsubscribe} className="ml-1 opacity-60 hover:opacity-100 transition-opacity">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="push-notification-prompt"
      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm"
      style={{ background: "rgba(234,179,8,0.07)", border: "1px solid rgba(234,179,8,0.18)" }}
    >
      <Bell className="w-5 h-5 text-yellow-400 flex-shrink-0" weight="fill" />
      <div className="flex-1 min-w-0">
        <span className="text-white/90 font-medium">Activa las notificaciones</span>
        <span className="text-white/50 ml-2 hidden sm:inline">
          — te avisamos cuando publiquemos una nueva jugada
        </span>
      </div>
      {error && !error.startsWith("iOS_NOT_STANDALONE") && (
        <span className="text-red-400 text-xs hidden sm:block">{error}</span>
      )}
      <button
        data-testid="push-enable-btn"
        onClick={subscribe}
        disabled={loading}
        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-60"
        style={{ background: "#EAB308", color: "#000" }}
      >
        {loading ? "Activando..." : "Activar"}
      </button>
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 text-white/30 hover:text-white/60 transition-colors"
        aria-label="Cerrar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
